import { TravelDocumentUploadValues } from "@/D2C/features/travel-quote/redux/type";
import { App_Url_New } from "@/shared/common/static";
import { createPostQuoteDocumentUploadStep } from "@/shared/components/PostQuoteSteps";
import {
  getNationalIdBackLabel,
  getNationalIdFrontLabel,
  getNationalIdLabel,
} from "@/shared/utils/nationalId";
import { CarDocumentUploadValues } from "../../redux/type";

const combinedGroup = (id: string, label: string, isPrimary = false) => ({
  id,
  label,
  checkboxLabel: "Both sides are in one file",
  combinedTitle: "Front & Back",
  combinedHelper: "Click to upload (PDF / JPG / PNG)",
  isPrimary,
});

// ==========================================
// TRAVEL CONFIGURATION
// ==========================================
const travelDefaultValues: TravelDocumentUploadValues = {
  emiratesIdFront: false,
  emiratesIdBack: false,
  emiratesIdCombined: false,
  passport: false,
  passportBack: false,
  visaCopy: false,
  confirmDocuments: false,
};

export const TravelDocumentUploadStep =
  createPostQuoteDocumentUploadStep<TravelDocumentUploadValues>({
    title: "Required Travel Documents",
    defaultValues: travelDefaultValues,
    confirmKey: "confirmDocuments",
    description: "Please upload your travel documents.",
    uploadEndpoint: App_Url_New.ApiEndPoint.documentUpload,
    ocrEndpointPath: App_Url_New.ApiOcrEndPoint.ExtractDocument,
    ocrFields: { ai_engine: "openai", ocr_engine: "easy" },
    fields: [
      {
        key: "emiratesIdFront",
        label: getNationalIdFrontLabel(),
        title: getNationalIdFrontLabel(),
        helper: "Click to upload (PDF / JPG / PNG)",
        required: false,
        icon: App_Url_New.DocumentIcons.idCard,
        ocrFields: App_Url_New.OcrApiFields.EMIRATES_ID,
        combinedUploadGroup: combinedGroup("travel-emirates-id", getNationalIdLabel(), true),
      },
      {
        key: "emiratesIdBack",
        label: getNationalIdBackLabel(),
        title: getNationalIdBackLabel(),
        helper: "Click to upload (PDF / JPG / PNG)",
        required: false,
        icon: App_Url_New.DocumentIcons.idCard,
        enableOcr: false,
        combinedUploadGroup: combinedGroup("travel-emirates-id", getNationalIdLabel()),
      },
      {
        key: "passportFront",
        label: "Passport Front",
        title: "Passport Front",
        helper: "Click to upload (PDF / JPG / PNG)",
        required: false,
        icon: App_Url_New.DocumentIcons.passport,
        enableOcr: false,
        combinedUploadGroup: combinedGroup("travel-passport", "Passport", true),
      },
      {
        key: "passportBack",
        label: "Passport Back",
        title: "Passport Back",
        helper: "Click to upload (PDF / JPG / PNG)",
        required: false,
        icon: App_Url_New.DocumentIcons.passport,
        enableOcr: false,
        combinedUploadGroup: combinedGroup("travel-passport", "Passport"),
      },
    ],
  });

// ==========================================
// CAR CONFIGURATION
// ==========================================
const defaultValues: CarDocumentUploadValues = {
  emiratesIdFront: false,
  emiratesIdBack: false,
  emiratesIdCombined: false,
  drivingLicenseFront: false,
  drivingLicenseBack: false,
  confirmDocuments: false,
  vehicleLicense: false,
  noClaimProof: false,
  otherDocument: false,
};

export const DocumentUploadStep = createPostQuoteDocumentUploadStep<CarDocumentUploadValues>({
  title: "Required Documents",
  defaultValues,
  confirmKey: "confirmDocuments",
  description: "",
  uploadEndpoint: App_Url_New.ApiEndPoint.documentUpload,
  ocrEndpointPath: App_Url_New.ApiOcrEndPoint.ExtractDocument,
  ocrFields: {
    ai_engine: "openai",
    ocr_engine: "easy",
  },
  fields: [
    {
      key: "emiratesIdFront",
      label: getNationalIdFrontLabel(),
      title: getNationalIdFrontLabel(),
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.idCard,
      ocrFields: App_Url_New.OcrApiFields.EMIRATES_ID,
      combinedUploadGroup: combinedGroup("emirates-id", getNationalIdLabel(), true),
    },
    {
      key: "emiratesIdBack",
      label: getNationalIdBackLabel(),
      title: getNationalIdBackLabel(),
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.idCard,
      enableOcr: false,
      combinedUploadGroup: combinedGroup("emirates-id", getNationalIdLabel()),
    },
    {
      key: "drivingLicenseFront",
      label: "Driving License Front",
      title: "Driving License Front",
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.passport,
      enableOcr: false,
      combinedUploadGroup: combinedGroup("driving-license", "Driving License", true),
    },
    {
      key: "drivingLicenseBack",
      label: "Driving License Back",
      title: "Driving License Back",
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.passport,
      enableOcr: false,
      combinedUploadGroup: combinedGroup("driving-license", "Driving License"),
    },
    {
      key: "vehicleLicense",
      label: "Vehicle License",
      title: "Vehicle License",
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.passport,
      ocrFields: App_Url_New.OcrApiFields.MOTOR,
    },
    {
      key: "noClaimProof",
      label: "No Claim Proof",
      title: "No Claim Proof",
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.passport,
      enableOcr: false,
    },
    {
      key: "otherDocument",
      label: "Other Document",
      title: "Other Document",
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.passport,
      enableOcr: false,
    },
  ],
});
